import { Injectable } from '@angular/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import { DemoPageRepoService } from '@client/app/modules/demodata/endpoints/pages/demo-page-repo.service';
import { DemoPdfRepoService } from '@client/app/modules/demodata/endpoints/demo-pdf-repo/demo-pdf-repo.service';
import { GlobalHelperService } from '@client/app/services/global-helper/global-helper.service';
import { PagePageLinksRepoService } from '@client/app/services/api/pages/page-links-repo.service';
import {
    PageAccessibilityRepoService,
} from '@client/app/services/api/page-accessibility/page-accessibility-repo.service';
import { PagePolicyRepoService } from '@client/app/services/api/page-policy/page-policy-repo.service';
import { PageClarityRepoService } from '@client/app/services/api/page-clarity/page-clarity-repo.service';
import { ENTITY_CONSTANT } from '@client/app/core/constants/entity.constant';
import { Page } from '@monsido/modules/page-details/models';


@Injectable({
    providedIn: 'root',
})
export class PageService {
    constructor (
        private pageRepoService: PageRepoService,
        private demoHasAccessService: DemoHasAccessService,
        private demoPageRepoService: DemoPageRepoService,
        private demoPdfRepoService: DemoPdfRepoService,
        private globalHelperService: GlobalHelperService,
        private pagePageLinksRepoService: PagePageLinksRepoService,
        private pageAccessibilityRepoService: PageAccessibilityRepoService,
        private pagePolicyRepoService: PagePolicyRepoService,
        private monEventService: MonEventService,
        private pageClarityRepoService: PageClarityRepoService,
    ) {}

    updateLink (pageId: number, linkId: number, data: any, params?: any) {
        return this.pagePageLinksRepoService.update(pageId, linkId, data, params);
    }

    get (page: Page | number, params?: any) {
        const isObject = this.globalHelperService.isObject(page);
        if (isObject && this.demoHasAccessService.canAccessAccessibilityPDF() && this.isDemo(page)) {
            return this.demoPageRepoService.get(page.id);
        }

        const pageId = isObject ? page.id : page;
        return this.pageRepoService.get(pageId, params).then(pageObj => {
            this.monEventService.run(ENTITY_CONSTANT.PAGE_UPDATED_EVENT, pageObj);
            return pageObj;
        });
    }

    update (page: any, data: any) {
        if (this.demoHasAccessService.canAccessAccessibilityPDF() && this.isDemo(page)) {
            return this.demoPageRepoService.update(page.id);
        }

        return this.pageRepoService.update(page.id, data).then(pageObj => {
            this.monEventService.run('updateRow', ENTITY_CONSTANT.PAGE, pageObj);
            return pageObj;
        });
    }

    getStatSummary (pageId: number, params?: any) {
        if (this.demoHasAccessService.useStatisticsDemoData()) {
            return this.demoPageRepoService.getStatSummary(pageId);
        }
        return this.pageRepoService.getStatSummary(pageId, params);
    }

    getStatVisitors (pageId: number, params?: any) {
        if (this.demoHasAccessService.useStatisticsDemoData()) {
            return this.demoPageRepoService.getStatVisitors(pageId);
        }
        return this.pageRepoService.getStatVisitors(pageId, params);
    }

    updateAccessibilityError (pageId: number, error: any, params?: any) {
        return this.pageAccessibilityRepoService.updateError(pageId, error, params);
    }

    deleteAccessibilityError (pageId: number, error: any) {
        return this.pageAccessibilityRepoService.markIssueAsFixed(pageId, error);
    }

    updateAccessibilityCheck (pageId: number, checkId: number, params?: any) {
        return this.pageAccessibilityRepoService.updateCheck(pageId, checkId, params);
    }

    updateAccessibilityCheckErrors (pageId: number, check: any) {
        return this.pageAccessibilityRepoService.updateCheckErrors(pageId, check);
    }

    deleteAccessibilityCheck (pageId: number, checkId: number) {
        return this.pageAccessibilityRepoService.markCheckAsFixed(pageId, checkId);
    }

    updateClarityCheckError (
        pageId: number,
        checkId: number,
        errorId: number,
        data: any,
    ) {
        return this.pageClarityRepoService.updateCheckError(pageId, checkId, errorId, data);
    }

    updatePolicy (pageId: number, data: any) {
        return this.pagePolicyRepoService.update(pageId, data);
    }

    destroyPolicy (pageId: number, id: number) {
        return this.pagePolicyRepoService.destroy(pageId, id);
    }

    updateSeoIssue (pageId: number, issueId: number, data: any) {
        return this.pageRepoService.updateSeoIssue(pageId, issueId, data);
    }

    rescanPageDetail (pageId: number) {
        return this.pageRepoService.rescan(pageId);
    }

    getCmsUrlPageDetail (pageId: number) {
        return this.pageRepoService.getCmsUrl(pageId);
    }

    private isDemo (page: any): boolean {
        return this.demoPageRepoService.isDemo(page) || this.demoPdfRepoService.isDemo(page);
    }
}
